import { useState, useEffect, useRef } from "react";
import { ApolloProvider } from "@apollo/client";
import UserContextProvider from "./Contexts/UserContext";
import App from "./App";
import { createTheme } from "@material-ui/core/styles";
import { ThemeProvider } from "@material-ui/styles";
import SetupContextProvider from "./Contexts/SetupContext";
import IncidentsContextProvider from "./Contexts/IncidentsContext";
import DashboardContextProvider from "./Contexts/DashboardContext";
import MutationContextProvider from "./Contexts/MutationContext";
import { CookiesProvider } from "react-cookie";
import getApolloClient from "./getApolloClient";
import * as serviceWorker from "./serviceWorker";
import { socket } from "./socket";
import { db as indexedDb } from "../src/IndexedDb/database";
import * as Sentry from "@sentry/react";
import { localGetData } from "./utils/utils";
import { getBuildMeta } from "./buildMeta";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";
import { createRoot } from "react-dom/client";

const theme = createTheme({
  palette: {
    primary: {
      main: "#393CED",
    },
    secondary: {
      main: "#8e24aa",
      // contrastText: '#ffcc00',
    },
    error: {
      main: "#ED393C",
    },
  },
});

const { commitHash } = getBuildMeta();
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.VITE_SENTRY_ENV || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || commitHash || undefined,
    denyUrls: [/localhost/],
    integrations: [
      new Sentry.BrowserTracing({
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
          /^https:\/\/testing\.valcourcloud-vt\.com:4000\/mp/,
          /^https:\/\/training\.valcourcloud-vt\.com:4001\/mp/,
        ],
      }),
      new Sentry.Replay(),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1, // Capture 100% of the transactions, reduce in production!
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    replays: {
      maxReplayDuration: 600, // 10 minutes
    },
  });
}

const Index = () => {
  const client = useRef(null);
  const subscriptionServer = useRef(null);
  const [loading, setLoading] = useState(true);
  const [onlineState, setOnlineState] = useState(true);
  const [timestamp, setTimestamp] = useState(null);
  const [socketConnected, setSocketConnected] = useState(true);
  const [errorObj, setErrorObj] = useState({ open: false, message: "" });
  const localUser = localGetData("mp_user");

  const rootBoundaryBeforeCapture = (scope, error, componentStack) => {
    scope.setTag("error_boundary", "root");
    scope.setExtra("componentStack", componentStack);
    scope.setExtra("onlineState", onlineState);
    scope.setExtra("socketConnected", socketConnected);
    scope.setExtra("timestamp", timestamp);
    scope.setExtra("errorMessage", error?.message || "Unknown error");
    scope.setExtra("userId", localUser?.id || null);
    scope.setExtra("homeOriId", localUser?.homeOriId || null);
  };

  const handleClose = () => {
    setErrorObj({ open: false, message: "" });
  };

  const toggleOnlineState = bool => {
    setOnlineState(bool);
  };

  useEffect(() => {
    getApolloClient(toggleOnlineState, setTimestamp, setErrorObj).then(
      localClient => {
        client.current = localClient;
        // console.log('setting the client')
        subscriptionServer.current = socket(setSocketConnected);
        setLoading(false);
      },
    );

    isStoragePersisted().then(async isPersisted => {
      if (isPersisted) {
        // console.log("Storage is successfully persisted.");
      } else {
        // console.log("Storage is not persisted.");
        // console.log("Trying to persist..:");
        //navigator.storage.estimate()
        if ((await navigator?.storage) && navigator.storage.persist()) {
          // console.log("We successfully turned the storage to be persisted.");
        } else {
          // console.log("Failed to make storage persisted");
        }
      }
    });
  }, []);

  async function isStoragePersisted() {
    return (
      (await navigator.storage) &&
      navigator.storage.persisted &&
      navigator.storage.persisted()
    );
  }

  function deleteOldLocalStorage() {
    let keys = Object.keys(localStorage);
    keys.forEach(key => {
      let item = localGetData(key);
      if (item && item.timestamp) {
        let now = new Date();
        let age = now - new Date(item.timestamp);
        let days = Math.floor(age / 864e5);
        if (days > 7) {
          console.log("Deleting old local storage item:", key);
          localStorage.removeItem(key);
        }
      }
    });
  }

  deleteOldLocalStorage();

  if (loading || !client || !subscriptionServer.current)
    return <div>Loading Valcour Mobile</div>;

  if (!loading && client && subscriptionServer.current)
    return (
      <ApolloProvider client={client.current}>
        <Sentry.ErrorBoundary
          beforeCapture={rootBoundaryBeforeCapture}
          fallback={
            <div style={{ padding: "16px", textAlign: "center" }}>
              Something went wrong. The error has been reported.
            </div>
          }
        >
          <CookiesProvider>
            <UserContextProvider
              subscriptionServer={subscriptionServer.current}
              onlineState={onlineState}
              timestamp={timestamp}
            >
              <SetupContextProvider>
                <DashboardContextProvider indexedDb={indexedDb}>
                  <IncidentsContextProvider>
                    <MutationContextProvider>
                      {/* Add SubscriptionContext here */}
                      <ThemeProvider theme={theme}>
                        <App
                          client={client.current}
                          loading={loading}
                          subscriptionServer={subscriptionServer.current}
                          timestamp={timestamp}
                          socketConnected={socketConnected}
                        />
                      </ThemeProvider>
                    </MutationContextProvider>
                  </IncidentsContextProvider>
                </DashboardContextProvider>
              </SetupContextProvider>
            </UserContextProvider>
          </CookiesProvider>
        </Sentry.ErrorBoundary>
        <Dialog
          open={errorObj.open}
          onClose={handleClose}
          aria-labelledby="graphql-network-error-title"
          aria-describedby="graphql-network-error-description"
        >
          <DialogTitle id="graphql-network-error-title">
            {"There was an error with a request"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="graphql-network-error-description">
              <strong>
                The {errorObj.message} operation did not complete. Please
                refresh and confirm that your data was saved.
              </strong>
              <br />
              <br />
              The error has been reported to the development team.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      </ApolloProvider>
    );
};

const container = document.getElementById("root");
const root = createRoot(container);
root.render(<Index />);
// render(<Index />, document.getElementById("root"));

// serviceWorker.register({
//   onUpdate(reg) {
//     // You can replace confirm() with a custom UI toast/dialog
//     const ok = window.confirm("A new version is available. Reload now?");
//     if (ok && reg.waiting) {
//       // Tell the waiting SW to become active
//       reg.waiting.postMessage({ type: "SKIP_WAITING" });
//       // Reload when it activates
//       reg.waiting.addEventListener("statechange", e => {
//         if (e.target.state === "activated") window.location.reload();
//       });
//     }
//   },
// });
serviceWorker.unregister();
